import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  Breadcrumbs,
  Link,
  Divider,
  Snackbar,
  Chip,
  MenuList,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  QuestionAnswer as VerifyIcon,
} from "@mui/icons-material";
import axios from "axios";
import { decryptText, encryptText, encryptAppId } from "../../utils/encryption";
import FileUploadComponent from "../FileUploadComponent";
import { getScrutinyRemarks, getClauseNo } from "../../services/api";
import paymentService from "../../services/paymentService";
import {
  insertDANC,
  updateDANCBySourceIdNewAppType,
  getDANC,
} from "../../services/apiCalibration";
import { TerminateApplicationDialog } from "../../components";

// AdequacyCheckSection Component for Acquisition/Merger Application
const AdequacyCheckSection = React.memo(
  ({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    handleAdequacySubmitted,
    loading,
    currentUser = "System User",
    currentUserId = localStorage.getItem("orgId") || 0,
  }) => {
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState("");
    const [dancSuccess, setDancSuccess] = useState("");
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);
    const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
    const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

    const currentUserTypeId = localStorage.getItem("userTypeId");
    const expectedNCType = "Document Adequacy";

    // Load existing DANC data on component mount
    useEffect(() => {
      const loadExistingDANCData = async () => {
        if (!orgId || !appId || dataLoaded) return;

        if (currentUserTypeId !== "4") return;
        if (!["15", "15a", "15b"].includes(applicationStage)) return;

        try {
          setDancLoading(true);

          const response = await getDANC({
            orgid: orgId,
            appid: appId,
            sectionName: "Acquisition Merger",
            subsectionName: title,
            isactive: 1,
          });

          if (response.data.success && response.data.data?.length > 0) {
            const filteredRecords = response.data.data.filter(
              (record) => record.NCtype === expectedNCType,
            );

            if (filteredRecords.length > 0) {
              const recordData = filteredRecords[0];
              console.log(`✅ Loading ${title} DANC:`, recordData);
              setExistingDANCRecord(recordData);

              if (recordData.InformationStatus) {
                handleAdequacyCheck(sectionId, recordData.InformationStatus);
              }

              const remarks =
                recordData.InformationProvided || recordData.Observation || "";
              if (remarks) {
                handleAdequacyRemarks(sectionId, remarks);
              }

              // Mark as submitted since existing record found
              if (handleAdequacySubmitted) {
                handleAdequacySubmitted(sectionId, true);
              }
            }
          }
          setDataLoaded(true);
        } catch (error) {
          console.log("Error loading DANC data:", error.message);
          setDataLoaded(true);
        } finally {
          setDancLoading(false);
        }
      };

      loadExistingDANCData();
    }, [
      orgId,
      appId,
      title,
      sectionId,
      dataLoaded,
      applicationStage,
      handleAdequacyCheck,
      handleAdequacyRemarks,
    ]);

    // Clear messages after timeout
    useEffect(() => {
      if (dancError || dancSuccess) {
        const timer = setTimeout(() => {
          setDancError("");
          setDancSuccess("");
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [dancError, dancSuccess]);

    const adequacy = adequacyChecks[sectionId] || { status: "", remarks: "" };

    // Fetch scrutiny remarks when status is Inadequate
    useEffect(() => {
      const fetchScrutinyRemarks = async () => {
        if (adequacy.status === "Inadequate") {
          try {
            setLoadingRemarks(true);
            const type = "Document Review";
            console.log(
              `🔍 Fetching scrutiny remarks for sectionName="${title}", type="${type}"`,
            );

            const response = await getScrutinyRemarks({
              sectionName: title,
              type,
            });

            console.log("📄 Scrutiny remarks response:", response.data);

            if (response.data.success) {
              const remarks = (response.data.data || []).map((r) => ({
                ...r,
                type,
              }));
              console.log(
                `✅ Found ${remarks.length} scrutiny remarks for "${title}"`,
              );
              setScrutinyRemarks(remarks);
            } else {
              console.log(`❌ No scrutiny remarks found for "${title}"`);
              setScrutinyRemarks([]);
            }
          } catch (error) {
            console.error("Error fetching scrutiny remarks:", error);
            setScrutinyRemarks([]);
          } finally {
            setLoadingRemarks(false);
          }
        } else {
          setScrutinyRemarks([]);
        }
      };

      fetchScrutinyRemarks();
    }, [adequacy.status, title]);

    if (
      !["15", "15a", "15b"].includes(applicationStage) ||
      currentUserTypeId !== "4"
    )
      return null;

    const isAdequacyReadOnly = applicationStage === "15a";

    const handleStatusChange = (status) => {
      handleAdequacyCheck(sectionId, status);
    };

    const handleRemarksChange = (remarks) => {
      handleAdequacyRemarks(sectionId, remarks);
    };

    const handleCheckboxChange = (option, checked) => {
      let newSelectedOptions;
      if (checked) {
        newSelectedOptions = [...selectedRemarksOptions, option];
      } else {
        newSelectedOptions = selectedRemarksOptions.filter(
          (item) => item.inadequate_remarks !== option.inadequate_remarks,
        );
      }
      setSelectedRemarksOptions(newSelectedOptions);

      const remarksText = newSelectedOptions
        .map((item) =>
          typeof item === "string" ? item : item.inadequate_remarks,
        )
        .join(", ");
      handleRemarksChange(remarksText);
    };

    const handleClearAllCheckboxes = () => {
      setSelectedRemarksOptions([]);
      handleRemarksChange("");
    };

    const handleSubmit = async () => {
      try {
        setDancLoading(true);
        setDancError("");
        setDancSuccess("");

        await handleAdequacySubmit(sectionId);

        if (adequacy.status) {
          if (existingDANCRecord) {
            const updateData = {
              subsectionName: title,
              informationStatus: adequacy.status,
              observation:
                adequacy.status === "Inadequate"
                  ? adequacy.remarks || "Document adequacy marked as Inadequate"
                  : "Document adequacy check completed",
              NCtype: expectedNCType,
              ClauseNo: null,
              isactive: 1,
              updatedby: localStorage.getItem("orgId") || 0,
            };

            const recordId = existingDANCRecord.Id || 0;
            const response = await updateDANCBySourceIdNewAppType(
              orgId,
              appId,
              "Acquisition Merger",
              recordId,
              0,
              0,
              updateData,
            );

            if (response.data.success) {
              setExistingDANCRecord(response.data.data);
              setDancSuccess("Record updated successfully");
              if (handleAdequacySubmitted) {
                handleAdequacySubmitted(sectionId, true);
              }
            } else {
              throw new Error(response.data.message || "Update failed");
            }
          } else {
            const dancData = {
              Orgid: parseInt(orgId),
              Appid: parseInt(appId),
              SectionName: "Acquisition Merger",
              SubsectionName: title,
              InformationProvided: adequacy.remarks || "",
              InformationStatus: adequacy.status,
              Observation:
                adequacy.status === "Inadequate"
                  ? adequacy.remarks || "Document adequacy marked as Inadequate"
                  : "Document adequacy check completed",
              assessorId: localStorage.getItem("orgId") || 0,
              Insertedby: localStorage.getItem("orgId") || 0,
              NCtype: expectedNCType,
              ClauseNo: null,
              assessmentId: 0,
            };

            const response = await insertDANC(dancData);
            if (response.data.success) {
              setExistingDANCRecord(response.data.data);
              setDancSuccess("Record created successfully");
              if (handleAdequacySubmitted) {
                handleAdequacySubmitted(sectionId, true);
              }
            } else {
              throw new Error(response.data.message || "Insert failed");
            }
          }
        }
      } catch (error) {
        console.error("Error submitting adequacy check:", error);
        setDancError("Error: " + error.message);
      } finally {
        setDancLoading(false);
      }
    };

    return (
      <Box
        sx={{
          mb: 3,
          p: 3,
          backgroundColor: "#f8f9fa",
          borderRadius: 2,
          border: "2px solid #e9ecef",
          borderLeft: "4px solid #007bff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#495057",
              fontWeight: 600,
              fontSize: "1.1rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📋 Adequacy Check - {title}
            {isAdequacyReadOnly && (
              <Chip
                label="🔒 Read Only"
                size="small"
                sx={{
                  bgcolor: "#f5f5f5",
                  color: "#757575",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                }}
              />
            )}
          </Typography>

          {adequacy.status && (
            <Chip
              label={`${adequacy.status}`}
              size="small"
              sx={{
                bgcolor: adequacy.status === "Adequate" ? "#28a745" : "#dc3545",
                color: "white",
                fontWeight: "bold",
              }}
            />
          )}
        </Box>

        {dancError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setDancError("")}
          >
            {dancError}
          </Alert>
        )}
        {dancSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setDancSuccess("")}
          >
            {dancSuccess}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Document Status</InputLabel>
              <Select
                value={adequacy.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                label="Document Status"
                disabled={isAdequacyReadOnly}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">-- Select Status --</MenuItem>
                <MenuItem value="Adequate">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon
                      sx={{ color: "#28a745", fontSize: "1rem" }}
                    />
                    Adequate
                  </Box>
                </MenuItem>
                <MenuItem value="Inadequate">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ErrorIcon sx={{ color: "#dc3545", fontSize: "1rem" }} />
                    Inadequate
                  </Box>
                </MenuItem>
                {currentUserTypeId === "4" && (
                  <MenuItem value="To be Verify">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VerifyIcon
                        sx={{ color: "#35dcdcff", fontSize: "1rem" }}
                      />
                      To be Verify
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {adequacy.status === "Inadequate" && (
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  isAdequacyReadOnly
                    ? "Remarks (View Only)"
                    : "Remarks (Required)"
                }
                placeholder={
                  isAdequacyReadOnly
                    ? "View Only"
                    : "Type your remarks here. Use dropdown below for suggestions."
                }
                value={adequacy.remarks || ""}
                onChange={(e) => handleRemarksChange(e.target.value)}
                required={!isAdequacyReadOnly}
                variant="outlined"
                disabled={isAdequacyReadOnly}
                sx={{
                  "& .MuiOutlinedInput-root": { backgroundColor: "white" },
                }}
              />

              {!isAdequacyReadOnly && scrutinyRemarks.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)
                    }
                    sx={{ mb: 1, borderColor: "#007bff", color: "#007bff" }}
                  >
                    {isCheckboxDropdownOpen ? "Hide" : "Show"} Predefined
                    Remarks ({scrutinyRemarks.length} available)
                  </Button>

                  {selectedRemarksOptions.length > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearAllCheckboxes}
                      sx={{ ml: 1, color: "#dc3545" }}
                    >
                      Clear All ({selectedRemarksOptions.length})
                    </Button>
                  )}

                  {isCheckboxDropdownOpen && (
                    <Paper
                      elevation={3}
                      sx={{
                        mt: 1,
                        maxHeight: 300,
                        overflow: "auto",
                        borderRadius: 2,
                      }}
                    >
                      <MenuList dense>
                        {scrutinyRemarks.map((option, index) => {
                          const remarkText =
                            typeof option === "string"
                              ? option
                              : option.inadequate_remarks;
                          const isSelected = selectedRemarksOptions.some(
                            (item) =>
                              (typeof item === "string"
                                ? item
                                : item.inadequate_remarks) === remarkText,
                          );

                          return (
                            <MenuItem
                              key={index}
                              onClick={() =>
                                handleCheckboxChange(option, !isSelected)
                              }
                              sx={{ py: 1, px: 2 }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{ p: 0, mr: 2 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {remarkText}
                              </Typography>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Paper>
                  )}
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  color: "#6c757d",
                  display: "block",
                  mt: 1,
                  fontStyle: "italic",
                }}
              >
                {loadingRemarks
                  ? "Loading remarks..."
                  : scrutinyRemarks.length > 0
                    ? `${scrutinyRemarks.length} predefined remark(s) available.`
                    : ""}
              </Typography>
            </Grid>
          )}
        </Grid>

        {adequacy.status && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSubmit}
              disabled={
                isAdequacyReadOnly ||
                !adequacy.status ||
                (adequacy.status === "Inadequate" &&
                  !adequacy.remarks?.trim()) ||
                loading ||
                dancLoading
              }
              sx={{
                background: existingDANCRecord
                  ? "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)"
                  : "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                color: "white",
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              {loading || dancLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  {existingDANCRecord ? "Updating..." : "Submitting..."}
                </Box>
              ) : existingDANCRecord ? (
                "🔄 Update"
              ) : (
                "📤 Submit"
              )}
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

const AcquisitionMerger = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedAppId = searchParams.get("appId");
  const encryptedLabTypeId = searchParams.get("labTypeId");

  // Decrypt parameters
  const decryptedOrgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const decryptedAppId = encryptedAppId ? decryptText(encryptedAppId) : null;
  const decryptedLabTypeId = encryptedLabTypeId
    ? decryptText(encryptedLabTypeId)
    : null;

  const [orgId, setOrgId] = useState(decryptedOrgId);
  const [appId, setAppId] = useState(decryptedAppId);
  const [labTypeId, setLabTypeId] = useState(decryptedLabTypeId);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("");
  const [userTypeId, setUserTypeId] = useState(
    localStorage.getItem("userTypeId") || "",
  );

  // Basic Details State
  const [labName, setLabName] = useState("");
  const [labType, setLabType] = useState("0");
  const [country, setCountry] = useState("101"); // Default to India
  const [state, setState] = useState("0");
  const [city, setCity] = useState("0");
  const [address, setAddress] = useState("");
  const [district, setDistrict] = useState("");
  const [pincode, setPincode] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [mobile, setMobile] = useState("");
  const [landlineCode, setLandlineCode] = useState("");
  const [landline, setLandline] = useState("");
  const [faxCode, setFaxCode] = useState("");
  const [fax, setFax] = useState("");
  const [email, setEmail] = useState("");

  // Change Details State
  const [newLabName, setNewLabName] = useState("");
  const [newGstin, setNewGstin] = useState("");
  const [changeAddress, setChangeAddress] = useState("");

  // Location Details State (when changeAddress = '1')
  const [locAddress, setLocAddress] = useState("");
  const [locCity, setLocCity] = useState("0");
  const [locDistrict, setLocDistrict] = useState("");
  const [locPincode, setLocPincode] = useState("");
  const [locCountryCode, setLocCountryCode] = useState("");
  const [locContact, setLocContact] = useState("");
  const [locFaxCode, setLocFaxCode] = useState("");
  const [locFax, setLocFax] = useState("");
  const [locEmail, setLocEmail] = useState("");

  // Accreditation Details State (when changeAddress = '0')
  const [fieldOfAccreditation, setFieldOfAccreditation] = useState("");
  const [currentAccrCycle, setCurrentAccrCycle] = useState("");
  const [acquiringName, setAcquiringName] = useState("");
  const [acquiringAddress, setAcquiringAddress] = useState("");
  const [noClaim, setNoClaim] = useState(false);
  const [remarks, setRemarks] = useState("");

  // File Upload States
  const [legalAgreementFile, setLegalAgreementFile] = useState(null);
  const [legalStatusFile, setLegalStatusFile] = useState(null);
  const [incorporationFile, setIncorporationFile] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [appendixAFile, setAppendixAFile] = useState(null);
  const [nabl131File, setNabl131File] = useState(null);
  const [remarksFile, setRemarksFile] = useState(null);

  // DO Scrutiny States
  const [scrutinyStatus, setScrutinyStatus] = useState("0");
  const [scrutinyRemarks, setScrutinyRemarks] = useState("");

  // Adequacy Checks State (unified pattern)
  const [adequacyChecks, setAdequacyChecks] = useState({});
  const [adequacySubmitted, setAdequacySubmitted] = useState({});

  // Dropdown Options
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locCities, setLocCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [locDistricts, setLocDistricts] = useState([]);
  const [districtName, setDistrictName] = useState("");

  // Dialog States
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [password, setPassword] = useState("");

  // Payment Status States
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [showPaymentButton, setShowPaymentButton] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'failed', 'success', 'pending'
  const [hasDataInDB, setHasDataInDB] = useState(false); // Track if data is saved in database

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Check payment status to determine Final Submit button visibility
  const checkPaymentStatus = async () => {
    try {
      if (!orgId || !appId) {
        console.log('⏭️ Skipping payment check - missing orgId or appId');
        return;
      }
      const response = await paymentService.checkPaymentStatus(orgId, appId);
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const hasSuccessfulPayment = response.data.some(payment => {
          const hasOrderStatus = payment.order_status?.toLowerCase() === 'success' || payment.order_status === 'Success';
          return hasOrderStatus;
        });
        const hasFailedPayment = response.data.some(payment =>
          payment.order_status?.toLowerCase() === 'failed'
        );
        if (hasSuccessfulPayment) {
          setPaymentStatus('success');
          setShowPaymentButton(false);
          const isStageValid = stage !== "15a" && stage !== "15" && stage !== "15b" && stage !== "15c";
          if (isStageValid && changeAddress !== "") {
            setShowSubmitButton(true);
          } else {
            setShowSubmitButton(false);
          }
        } else if (hasFailedPayment) {
          setPaymentStatus('failed');
          setShowSubmitButton(false);
          const isStageValid = stage !== "15a" && stage !== "15" && stage !== "15b" && stage !== "15c";
          
          // Check if mandatory fields are filled AND data is saved in database
          let isDataFilled = false;
          if (changeAddress && changeAddress !== "" && hasDataInDB) {
            if (changeAddress === "0") {
              // For "No Change", only noClaim is required
              isDataFilled = noClaim;
            } else {
              // For "Change in Address", changeAddress must be selected (not "0")
              isDataFilled = changeAddress !== "0";
            }
          }
          
          setShowPaymentButton(isStageValid && isDataFilled);
        } else {
          setPaymentStatus('pending');
          setShowSubmitButton(false);
          setShowPaymentButton(false);
        }
      } else {
        setPaymentStatus(null);
        setShowSubmitButton(false);
        const isReadOnlyStage = stage === "15" || stage === "15a" || stage === "15b" || stage === "15c";
        
        // Check if mandatory fields are filled AND data is saved in database
        let isDataFilled = false;
        if (changeAddress && changeAddress !== "" && hasDataInDB) {
          if (changeAddress === "0") {
            // For "No Change", only noClaim is required
            isDataFilled = noClaim;
          } else {
            // For "Change in Address", changeAddress must be selected (not "0")
            isDataFilled = changeAddress !== "0";
          }
        }
        
        const shouldShowPayment = !isReadOnlyStage && isDataFilled;
        setShowPaymentButton(shouldShowPayment);
      }
    } catch (error) {
      setPaymentStatus(null);
      setShowSubmitButton(false);
      const isReadOnlyStage = stage === "15" || stage === "15a" || stage === "15b" || stage === "15c";
      
      // Check if mandatory fields are filled AND data is saved in database
      let isDataFilled = false;
      if (changeAddress && changeAddress !== "" && hasDataInDB) {
        if (changeAddress === "0") {
          // For "No Change", only noClaim is required
          isDataFilled = noClaim;
        } else {
          // For "Change in Address", changeAddress must be selected (not "0")
          isDataFilled = changeAddress !== "0";
        }
      }
      
      setShowPaymentButton(!isReadOnlyStage && isDataFilled);
    }
  };

  // Unified Adequacy Check Handlers
  const handleAdequacyCheck = useCallback((sectionId, value) => {
    setAdequacyChecks((prev) => {
      const current = prev[sectionId] || {};
      return {
        ...prev,
        [sectionId]: {
          ...current,
          status: value,
          remarks: value === "Adequate" ? "" : current.remarks || "",
        },
      };
    });
  }, []);

  const handleAdequacyRemarks = useCallback((sectionId, remarks) => {
    setAdequacyChecks((prev) => {
      const current = prev[sectionId] || {};
      if (current.remarks === remarks) {
        return prev;
      }
      return {
        ...prev,
        [sectionId]: {
          ...current,
          remarks: remarks,
        },
      };
    });
  }, []);

  const handleAdequacySubmit = useCallback(async (sectionId) => {
    console.log(`Submitting adequacy check for section: ${sectionId}`);
    // The actual DANC API calls are handled within AdequacyCheckSection component
    return Promise.resolve();
  }, []);

  const handleAdequacySubmitted = useCallback((sectionId, isSubmitted) => {
    setAdequacySubmitted((prev) => ({
      ...prev,
      [sectionId]: isSubmitted,
    }));
  }, []);

  // Helper function to safely extract filename from file objects
  const extractFileName = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file;
    if (typeof file === "object" && file.fileName) return file.fileName;
    return "";
  };

  // Load data on mount
  useEffect(() => {
    if (orgId) {
      initializePage();
    }
  }, [orgId]);

  // Reset district name when state or district changes
  useEffect(() => {
    setDistrictName("");
  }, [state, district]);

  // Load district name when states and district are available
  useEffect(() => {
    const loadDistrictName = async () => {
      if (states.length > 0 && state && state !== "0" && district) {
        // Find the state object to get State_Code
        const stateObj = states.find(
          (s) => s.id?.toString() === state?.toString(),
        );
        if (!stateObj || !stateObj.State_Code) {
          console.log("State_Code not found for state id:", state);
          return;
        }

        const stateCode = stateObj.State_Code;
        const districtsList = await loadDistricts(stateCode);
        // Try matching by District_Code first (API returns District_Code)
        let districtObj = districtsList.find(
          (d) =>
            d.District_Code === parseInt(district) ||
            d.District_Code === district,
        );

        // Fallback to id field match
        if (!districtObj) {
          districtObj = districtsList.find(
            (d) => d.id === parseInt(district) || d.id === district,
          );
        }

        // Fallback to exact name match (case-insensitive)
        if (!districtObj && typeof district === "string") {
          districtObj = districtsList.find(
            (d) =>
              d.District_Name_Eng?.toLowerCase().trim() ===
              district.toLowerCase().trim(),
          );
        }

        // Fallback to partial name match
        if (!districtObj && typeof district === "string") {
          districtObj = districtsList.find(
            (d) =>
              d.District_Name_Eng?.toLowerCase().includes(
                district.toLowerCase(),
              ) ||
              district
                .toLowerCase()
                .includes(d.District_Name_Eng?.toLowerCase()),
          );
        }

        if (districtObj) {
          console.log("✓ Found district:", districtObj.District_Name_Eng);
          setDistrictName(districtObj.District_Name_Eng);
        } else {
          console.log("✗ No district match found for:", district);
        }
      } else {
        console.log("Conditions not met:", {
          hasStates: states.length > 0,
          hasState: state && state !== "0",
          hasDistrict: !!district,
          hasNoDistrictName: !districtName,
        });
      }
    };
    loadDistrictName();
  }, [states, state, district]);

  const initializePage = async () => {
    try {
      setLoading(true);
      // Load sequentially to ensure dropdowns are populated before values are set
      await loadStates(101); // Load India's states first
      await loadBasicDetails(); // Load basic details

      // Now load functions that depend on appId in parallel
      if (appId) {
        await Promise.all([
          loadLocationDetails(), // Then load location details with values
          loadStage(),
        ]);
      }
    } catch (err) {
      console.error("Error initializing page:", err);
      showSnackbar("Failed to load application data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Re-check payment status when changeAddress or stage changes
  useEffect(() => {
    if (orgId && appId) {
      checkPaymentStatus();
    }
  }, [changeAddress, stage]);

  // Re-check payment status when noClaim changes
  useEffect(() => {
    if (orgId && appId) {
      checkPaymentStatus();
    }
  }, [noClaim, hasDataInDB]);

  const loadStates = async (countryId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/states`,
      );
      if (response.data.success) {
        setStates(response.data.data || []);
      }
    } catch (err) {
      console.error("Error loading states:", err);
    }
  };

  const loadCities = async (stateId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/cities/${stateId}`,
      );
      if (response.data.success) {
        const citiesData = response.data.data || [];
        setCities(citiesData);
        setLocCities(citiesData);
      }
    } catch (err) {
      console.error("Error loading cities:", err);
    }
  };

  const loadDistricts = async (stateCode) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/districts/${stateCode}`,
      );
      if (response.data.success) {
        const districtsData = response.data.data || [];
        setDistricts(districtsData);
        setLocDistricts(districtsData);
        return districtsData;
      }
      return [];
    } catch (err) {
      console.error("Error loading districts:", err);
      return [];
    }
  };

  const loadStage = async () => {
    try {
      if (!appId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/application-stage?appid=${appId}`,
      );
      if (response.data.success) {
        setStage(response.data.data.stage || "");
      }
    } catch (err) {
      console.error("Error loading stage:", err);
    }
  };

  const loadBasicDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-name-app/main-lab-details/${orgId}`,
      );
      if (response.data.success) {
        const data = response.data.data;

        setLabName(data.CabName || "");

        // Set LabType directly (Diagnostic, Calibration, Medical)
        setLabType(data.CabType || "0");

        setCountry(
          data.CountryId?.toString() || data.Country?.toString() || "101",
        );
        setState(data.stateId?.toString() || data.State?.toString() || "0");
        setCity(data.cityId?.toString() || data.City?.toString() || "0");

        setAddress(data.Address || "");
        setDistrict(data.District?.toString() || "");
        setPincode(data.Pincode || "");
        setMobile(data.MobileNo || "");
        setEmail(data.Email || "");

        // Parse Landline if it has format like "Code-Number"
        if (data.LandlineNo) {
          const landlineParts = data.LandlineNo.split("-");
          if (landlineParts.length === 2) {
            setLandlineCode(landlineParts[0]);
            setLandline(landlineParts[1]);
          } else {
            setLandline(data.LandlineNo);
          }
        }

        // Parse Fax if it has format like "Code-Number"
        if (data.FaxNo) {
          const faxParts = data.FaxNo.split("-");
          if (faxParts.length === 2) {
            setFaxCode(faxParts[0]);
            setFax(faxParts[1]);
          } else {
            setFax(data.FaxNo);
          }
        }

        // Load dependent dropdowns
        if (data.stateId || data.State) {
          await loadCities(data.stateId || data.State);

          // Load districts if we have state
          const stateObj = states.find(
            (s) =>
              s.id?.toString() === (data.stateId || data.State)?.toString(),
          );
          if (stateObj && stateObj.State_Code) {
            await loadDistricts(stateObj.State_Code);
          }
        }
      }
    } catch (err) {
      console.error("Error loading basic details:", err);
    }
  };

  const loadLocationDetails = async () => {
    try {
      if (!appId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/acquisition-merger/${appId}/${orgId}`,
      );
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setHasDataInDB(true); // Mark that data exists in database
        setNewLabName(data.NewLabName || data.newLabName || "");
        setNewGstin(data.GSTIN || data.gstin || "");

        // Convert AddressChange to string for proper comparison
        const addressChangeValue =
          data.AddressChange !== null && data.AddressChange !== undefined
            ? data.AddressChange.toString()
            : data.addressChange || "";
        setChangeAddress(addressChangeValue);

        // Location details - handle both uppercase and lowercase field names
        setLocAddress(data.Address || data.address || "");
        setLocCity((data.City || data.city)?.toString() || "0");
        setLocDistrict(data.District || data.district || "");
        setLocPincode(data.Pincode || data.pincode || "");
        setLocEmail(data.Email || data.email || "");

        // Parse location contact - handle both ~ and - delimiters
        // const contactData = data.Contact || data.contact;
        // if (contactData && contactData !== "-" && contactData !== null) {
        //   const parts = contactData.split(/[~-]/);
        //   setLocCountryCode(parts[0] || "");
        //   setLocContact(parts[1] || "");
        // }

        // // Parse location fax - handle both ~ and - delimiters
        // const faxData = data.Fax || data.fax;
        // if (faxData && faxData !== "-" && faxData !== null) {
        //   const parts = faxData.split(/[~-]/);
        //   setLocFaxCode(parts[0] || "");
        //   setLocFax(parts[1] || "");
        // }

        setLocContact(data.Contact || data.contact || "");
        setLocFax(data.Fax || data.fax || "");

        // Accreditation details - handle both uppercase and lowercase field names
        setFieldOfAccreditation(data.Fieldof_Accr || data.fieldOfAccr || "");
        setCurrentAccrCycle(data.currAccr_cycle || data.currAccrCycle || "");
        setAcquiringName(data.AcquiringName || data.acquiringName || "");
        setAcquiringAddress(data.addOfAccuiring || "");
        setRemarks(data.remarksByCab || "");
        setNoClaim(data.is_DeclaredByCab === 1 || data.isDeclaredByCab === 1);

        // Set file references - handle both uppercase and lowercase field names
        if (data.legValdAgre_File || data.legValdAgreFile)
          setLegalAgreementFile(
            createFileObject(data.legValdAgre_File || data.legValdAgreFile),
          );
        if (data.docCitingAcqui_File || data.docCitingAcquiFile)
          setLegalStatusFile(
            createFileObject(
              data.docCitingAcqui_File || data.docCitingAcquiFile,
            ),
          );
        if (data.CertiOfIncorp_File || data.certiOfIncorpFile)
          setIncorporationFile(
            createFileObject(data.CertiOfIncorp_File || data.certiOfIncorpFile),
          );
        if (data.EvideOfNewComp_File || data.evideOfNewCompFile)
          setEvidenceFile(
            createFileObject(
              data.EvideOfNewComp_File || data.evideOfNewCompFile,
            ),
          );
        if (data.AppendixA_File || data.appendixAFile)
          setAppendixAFile(
            createFileObject(data.AppendixA_File || data.appendixAFile),
          );
        if (data.NABL131_File || data.nabl131File)
          setNabl131File(
            createFileObject(data.NABL131_File || data.nabl131File),
          );
        if (data.fileUploadWithRemarks)
          setRemarksFile(createFileObject(data.fileUploadWithRemarks));
      } else {
        setHasDataInDB(false); // No data in database
      }
    } catch (err) {
      console.error("Error loading location details:", err);
      setHasDataInDB(false); // Error means no data
    }
  };

  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName,
      key: `live/${fileName}`, // Just the filename, backend will add live/ prefix
      originalName: fileName,
    };
  };

  const handleCountryChange = async (value) => {
    // Country is fixed to India, no change allowed
    setCountry("101");
    setState("0");
    setCity("0");
  };

  const handleStateChange = async (value) => {
    setState(value);
    setCity("0");
    setLocCity("0");
    if (value !== "0") {
      await loadCities(value);

      // Load districts for the selected state
      const stateObj = states.find(
        (s) => s.id?.toString() === value?.toString(),
      );
      if (stateObj && stateObj.State_Code) {
        await loadDistricts(stateObj.State_Code);
      }
    }
  };

  const handleSaveDraft = async () => {
    try {
      // Validate based on changeAddress value
      if (!newLabName.trim()) {
        showSnackbar("Please enter new laboratory name", "warning");
        return false;
      }

      if (!changeAddress) {
        showSnackbar("Please select change in address option", "warning");
        return false;
      }

      if (changeAddress === "0" && !noClaim) {
        showSnackbar("Please check the No Claim checkbox", "warning");
        return false;
      }

      const formData = {
        appid: appId,
        orgid: orgId,
        labtypeid: labTypeId || "3",
        newLabName: newLabName,
        gstin: newGstin,
        addressChange: changeAddress,
        createdBy: localStorage.getItem("orgId") || null,
      };

      if (changeAddress === "1") {
        // Location details
        formData.address = locAddress;
        formData.city = locCity;
        formData.district = locDistrict;
        formData.pincode = locPincode;
        //formData.contact = `${locCountryCode}~${locContact}`;
        //formData.fax = `${locFaxCode}~${locFax}`;
        formData.contact = locContact;
        formData.fax = locFax;
        formData.email = locEmail;
      } else {
        // Accreditation details (for merger/acquisition)
        formData.fieldofAccr = fieldOfAccreditation;
        formData.currAccrCycle = currentAccrCycle;
        formData.acquiringName = acquiringName;
        formData.addOfAccuiring = acquiringAddress;
        // File uploads - extract filename strings
        formData.legValdAgreFile = extractFileName(legalAgreementFile);
        formData.docCitingAcquiFile = extractFileName(legalStatusFile);
        formData.certiOfIncorpFile = extractFileName(incorporationFile);
        formData.evideOfNewCompFile = extractFileName(evidenceFile);
        formData.appendixAFile = extractFileName(appendixAFile);
        formData.nabl131File = extractFileName(nabl131File);
        formData.remarksByCab = remarks;
        formData.fileUploadWithRemarks = extractFileName(remarksFile);
        formData.isDeclaredByCab = noClaim ? 1 : 0;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/acquisition-merger`,
        formData,
      );

      if (response.data.success) {
        showSnackbar("Data saved successfully", "success");
        await loadLocationDetails();
        await checkPaymentStatus(); // Re-check payment status after save
        return true;
      } else {
        showSnackbar(response.data.message || "Failed to save data", "error");
        return false;
      }
    } catch (err) {
      console.error("Error saving:", err);
      showSnackbar("Error saving data", "error");
      return false;
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: orgId,
          password: password,
        },
      );

      if (!passwordResponse.data.success) {
        showSnackbar("Invalid password", "error");
        return;
      }

      // Execute action based on dialog type
      if (dialogType === "final-submit") {
        await handleFinalSubmit();
      } else if (dialogType === "payment") {
        navigate(`/main/medical/payment?paymentType=${encryptText('2')}&appId=${encryptAppId(appId)}`);
      } else if (dialogType === "send-to-nabl") {
        await handleSendToNABL();
      } else if (dialogType === "send-to-cab") {
        await handleSendToCab();
      } else if (dialogType === "generate-form11") {
        await handleGenerateForm11();
      } else if (dialogType === "close-nc") {
        await handleCloseNC();
      } else if (dialogType === "generate-form-11") {
        await handleGenerateForm11();
      } else if (dialogType === "go-for-assessment") {
        await handleGoForAssessment();
      } else if (dialogType === "process-completed") {
        await handleProcessCompleted();
      }

      setShowPasswordDialog(false);
      setPassword("");
    } catch (err) {
      console.error("Error with password submit:", err);
      showSnackbar("Error processing request", "error");
    }
  };

  const handleFinalSubmit = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/final-submit`,
        {
          appId: appId,
          orgId: orgId,
        },
      );

      if (response.data.success) {
        showSnackbar("Application submitted successfully", "success");
         setTimeout(() => {
          navigate(`/main/preview-report?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&observation=${encryptText('False')}&autoDownload=true&fromSubmission=true`);
        }, 1500);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        showSnackbar(response.data.message || "Failed to submit", "error");
      }
    } catch (err) {
      console.error("Error in final submit:", err);
      showSnackbar("Error submitting application", "error");
    }
  };

  const handleSendToNABL = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/send-to-nabl`,
        {
          appId: appId,
          orgId: orgId,
        },
      );

      if (response.data.success) {
        showSnackbar("Application sent to NABL successfully", "success");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        showSnackbar(response.data.message || "Failed to send", "error");
      }
    } catch (err) {
      console.error("Error sending to NABL:", err);
      showSnackbar("Error sending to NABL", "error");
    }
  };

  const handleSendToCab = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/send-to-cab`,
        {
          appId: appId,
          orgId: orgId,
        },
      );

      if (response.data.success) {
        showSnackbar("Sent to CAB successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 4000);
      } else {
        showSnackbar(response.data.message || "Failed to send", "error");
      }
    } catch (err) {
      console.error("Error sending to CAB:", err);
      showSnackbar("Error sending to CAB", "error");
    }
  };

  const handleCloseNC = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "15c",
        },
      );

      if (response.data.success) {
        showSnackbar("NC closed successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(response.data.message || "Failed to close NC", "error");
      }
    } catch (err) {
      console.error("Error closing NC:", err);
      showSnackbar("Error closing NC", "error");
    }
  };

  const handleGoForAssessment = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "19",
        },
      );

      if (response.data.success) {
        showSnackbar("Application moved to assessment successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(
          response.data.message || "Failed to move application to assessment",
          "error",
        );
      }
    } catch (err) {
      console.error("Error moving application to assessment:", err);
      showSnackbar("Error moving application to assessment", "error");
    }
  };

  const handleGenerateForm11 = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "15c",
        },
      );

      if (response.data.success) {
        showSnackbar("Form 11 generated successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(
          response.data.message || "Failed to generate Form 11",
          "error",
        );
      }
    } catch (err) {
      console.error("Error generating Form 11:", err);
      showSnackbar("Error generating Form 11", "error");
    }
  };

  const handleProcessCompleted = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/acquisition-merger-app/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "15d",
        },
      );

      if (response.data.success) {
        showSnackbar("Process completed successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(
          response.data.message || "Failed to complete the process",
          "error",
        );
      }
    } catch (err) {
      console.error("Error completing the process:", err);
      showSnackbar("Error completing the process", "error");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const downloadNABLFormat = () => {
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_PAGE_URL}/Declaration_Acquisition_Merger.pdf`;
    link.download = "Declaration_Acquisition_Merger.pdf";
    link.click();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Acquisition/Merger Form...
        </Typography>
      </Box>
    );
  }

  const isReadOnly = stage === "15" || stage === "15b" || stage === "15c";

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc", pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackClick}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Applications
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Acquisition/Merger/Takeover
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Acquisition, Merger, Take over and purchase / selling of CAB
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: 3 }}>
        {/* Basic Laboratory Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Laboratory Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name of the Laboratory"
                  value={labName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type of Laboratory</InputLabel>
                  <Select value={labType} disabled label="Type of Laboratory">
                    <MenuItem value="0">--Select Type--</MenuItem>
                    <MenuItem value="Diagnostic">
                      Medical Testing Laboratory
                    </MenuItem>
                    <MenuItem value="Medical Imaging">Medical Imaging</MenuItem>
                    <MenuItem value="Both">
                      Medical Testing Laboratory + Medical Imaging
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select value={country} disabled label="Country">
                    <MenuItem value="101">India</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select value={state} disabled label="State">
                    <MenuItem value="0">--Select State--</MenuItem>
                    {states.map((s) => (
                      <MenuItem key={s.id} value={s.id?.toString() || ""}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>City</InputLabel>
                  <Select value={city} disabled label="City">
                    <MenuItem value="0">--Select City--</MenuItem>
                    {cities.map((c) => (
                      <MenuItem key={c.id} value={c.id?.toString() || ""}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={address}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="District"
                  value={districtName || district}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  value={pincode}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mobile No"
                  value={mobile}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Code"
                      value={landlineCode}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '100px' }}
                    />
                    <TextField
                      fullWidth
                      label="Landline no"
                      value={landline}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Code"
                      value={faxCode}
                      InputProps={{ readOnly: true }}
                      sx={{ width: '100px' }}
                    />
                    <TextField
                      fullWidth
                      label="Fax No."
                      value={fax}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>
                </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="E-mail"
                  value={email}
                  InputProps={{ readOnly: true }}
                />
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>

        {/* New Name & Address Change */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Proposed New Name and Change in Address
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Proposed New Name of the Laboratory"
                  value={newLabName}
                  onChange={(e) => setNewLabName(e.target.value)}
                  disabled={isReadOnly}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="GSTIN"
                  value={newGstin}
                  onChange={(e) => setNewGstin(e.target.value)}
                  disabled={isReadOnly}
                  helperText="(if not applicable then write NA)"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Change in Address ?<span style={{ color: "red" }}>*</span>
                </Typography>
                <RadioGroup
                  row
                  value={changeAddress}
                  onChange={(e) => setChangeAddress(e.target.value)}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio disabled={isReadOnly} />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="0"
                    control={<Radio disabled={isReadOnly} />}
                    label="No"
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Document Adequacy - Proposed New Name and Change in Address */}
        <AdequacyCheckSection
          sectionId="proposed-new-name-address"
          title="Proposed New Name and Change in Address"
          applicationStage={stage}
          orgId={orgId}
          appId={appId}
          adequacyChecks={adequacyChecks}
          handleAdequacyCheck={handleAdequacyCheck}
          handleAdequacyRemarks={handleAdequacyRemarks}
          handleAdequacySubmit={handleAdequacySubmit}
          handleAdequacySubmitted={handleAdequacySubmitted}
          loading={loading}
          currentUserId={localStorage.getItem("orgId")}
        />

        {/* Location Details (when changeAddress = '1') */}
        {changeAddress === "1" && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Location Details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Address"
                    value={locAddress}
                    onChange={(e) => setLocAddress(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>City</InputLabel>
                    <Select
                      value={locCity}
                      onChange={(e) => setLocCity(e.target.value)}
                      disabled={isReadOnly}
                      label="City"
                    >
                      <MenuItem value="0">--Select City--</MenuItem>
                      {locCities.map((c) => (
                        <MenuItem key={c.id} value={c.id?.toString() || ""}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>District</InputLabel>
                    <Select
                      value={locDistrict}
                      onChange={(e) => setLocDistrict(e.target.value)}
                      disabled={isReadOnly}
                      label="District"
                    >
                      <MenuItem value="">--Select District--</MenuItem>
                      {locDistricts.map((d) => (
                        <MenuItem
                          key={d.District_Code || d.id}
                          value={
                            d.District_Code?.toString() ||
                            d.id?.toString() ||
                            ""
                          }
                        >
                          {d.District_Name_Eng || d.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Pin Code"
                    value={locPincode}
                    onChange={(e) => setLocPincode(e.target.value)}
                    disabled={isReadOnly}
                    inputProps={{ maxLength: 6 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* <TextField
                      label="Code"
                      value={locCountryCode}
                      onChange={(e) => setLocCountryCode(e.target.value)}
                      disabled={isReadOnly}
                      sx={{ width: "100px" }}
                    /> */}
                    <TextField
                      fullWidth
                      required
                      label="Contact No"
                      value={locContact}
                      onChange={(e) => setLocContact(e.target.value)}
                      disabled={isReadOnly}
                      inputProps={{ maxLength: 11 }}
                      placeholder="Contact No"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* <TextField
                      label="Code"
                      value={locFaxCode}
                      onChange={(e) => setLocFaxCode(e.target.value)}
                      disabled={isReadOnly}
                      sx={{ width: "100px" }}
                    /> */}
                    <TextField
                      fullWidth
                      label="Fax No."
                      value={locFax}
                      onChange={(e) => setLocFax(e.target.value)}
                      disabled={isReadOnly}
                      inputProps={{ maxLength: 10 }}
                      placeholder="Fax No"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="E-mail"
                    value={locEmail}
                    onChange={(e) => setLocEmail(e.target.value)}
                    disabled={isReadOnly}
                    type="email"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Accreditation Details (when changeAddress = '0') */}
        {changeAddress === "0" && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Accreditation
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Field of Accreditation"
                    value={fieldOfAccreditation}
                    onChange={(e) => setFieldOfAccreditation(e.target.value)}
                    disabled={isReadOnly}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Accreditation Cycle"
                    value={currentAccrCycle}
                    onChange={(e) => setCurrentAccrCycle(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name of the Acquiring (or) Merger Firm / Company/ Entity:"
                    value={acquiringName}
                    onChange={(e) =>
                      setAcquiringName(e.target.value.toUpperCase())
                    }
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Address of the Acquiring (or) Merger Firm / Company/ Entity:"
                    value={acquiringAddress}
                    onChange={(e) =>
                      setAcquiringAddress(e.target.value.toUpperCase())
                    }
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Document Adequacy - Accreditation */}
        {changeAddress === "0" && (
          <AdequacyCheckSection
            sectionId="accreditation"
            title="Accreditation"
            applicationStage={stage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            handleAdequacySubmitted={handleAdequacySubmitted}
            loading={loading}
            currentUserId={localStorage.getItem("orgId")}
          />
        )}

        {/* Document Uploads (when changeAddress = '0') */}
        {changeAddress === "0" && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Document Uploads
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FileUploadComponent
                    fieldName="legalAgreement"
                    label="Valid agreement between both parties notarised for Acquisition on stamp paper of Rs 300/-:"
                    orgId={orgId}
                    section="acquisition-merger"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setLegalAgreementFile(file)}
                    initialFile={legalAgreementFile}
                    required
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FileUploadComponent
                    fieldName="legalStatus"
                    label="Document citing the current Legal status of the accredited CAB after 'Acquisition/Takeover' or 'Merger'."
                    orgId={orgId}
                    section="acquisition-merger"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setLegalStatusFile(file)}
                    initialFile={legalStatusFile}
                    required
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FileUploadComponent
                    fieldName="incorporation"
                    label="Certificate of Incorporation or articles of association ( if applicable)"
                    orgId={orgId}
                    section="acquisition-merger"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setIncorporationFile(file)}
                    initialFile={incorporationFile}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FileUploadComponent
                    fieldName="evidence"
                    label="Declaration that new company."
                    orgId={orgId}
                    section="acquisition-merger"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setEvidenceFile(file)}
                    initialFile={evidenceFile}
                    required
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}
                  >
                    Duly signed copy of following declarations needs to be
                    uploaded:
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={downloadNABLFormat}
                    sx={{ mb: 1 }}
                  >
                    Download Format
                  </Button>
                  <FileUploadComponent
                    fieldName="appendixA"
                    label="Duly signed declaration"
                    orgId={orgId}
                    section="acquisition-merger"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setAppendixAFile(file)}
                    initialFile={appendixAFile}
                    required
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FileUploadComponent
                    fieldName="nabl131"
                    label="131 declaration"
                    orgId={orgId}
                    section="acquisition-merger"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setNabl131File(file)}
                    initialFile={nabl131File}
                    required
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={noClaim}
                        onChange={(e) => setNoClaim(e.target.checked)}
                        disabled={isReadOnly}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        CAB shall not claim to be NABL accredited and shall not
                        use NABL symbol till the new name is approved by NABL.{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Typography>
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Remarks,if any:"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FileUploadComponent
                    fieldName="remarksFile"
                    label="Upload File, if any"
                    orgId={orgId}
                    section="acquisition-merger"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setRemarksFile(file)}
                    initialFile={remarksFile}
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Document Adequacy - Document Uploads */}
        {changeAddress === "0" && (
          <AdequacyCheckSection
            sectionId="document-uploads"
            title="Document Uploads"
            applicationStage={stage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            handleAdequacySubmitted={handleAdequacySubmitted}
            loading={loading}
            currentUserId={localStorage.getItem("orgId")}
          />
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
          {!isReadOnly && changeAddress && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSaveDraft}
              size="large"
            >
              Save
            </Button>
          )}

          {showSubmitButton && !isReadOnly && (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setDialogType("final-submit");
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Final Submit
            </Button>
          )}

          {showPaymentButton && !isReadOnly && (
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                setDialogType('payment');
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Proceed for Payment
            </Button>
          )}

          {/* {userTypeId === "4" && (stage === "15" || stage === "15b") && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                setDialogType("close-nc");
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Close NC
            </Button>
          )} */}
          {userTypeId === "4" &&
            (stage === "15" || stage === "15b") &&
            // Show Send to CAB button only when adequacy checks are submitted AND at least one is Inadequate
            adequacySubmitted["proposed-new-name-address"] &&
            (changeAddress === "1" ||
              (adequacySubmitted["accreditation"] &&
                adequacySubmitted["document-uploads"])) &&
            (adequacyChecks["proposed-new-name-address"]?.status ===
              "Inadequate" ||
              (changeAddress === "0" &&
                (adequacyChecks["accreditation"]?.status === "Inadequate" ||
                  adequacyChecks["document-uploads"]?.status ===
                    "Inadequate"))) && (
              <Button
                variant="contained"
                color="info"
                onClick={() => {
                  setDialogType("send-to-cab");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Send to CAB
              </Button>
            )}
          {userTypeId !== "4" && stage === "15a" && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setDialogType("send-to-nabl");
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Send to NABL
            </Button>
          )}

          {userTypeId === "4" && (stage === "15" || stage === "15b") && adequacySubmitted["proposed-new-name-address"] &&
            (changeAddress === "1" ||
              (adequacySubmitted["accreditation"] &&
                adequacySubmitted["document-uploads"])) &&
            // Check if all sections are Adequate or To be Verify
            (() => {
              const sectionsToCheck = ["proposed-new-name-address"];
              if (changeAddress === "0") {
                sectionsToCheck.push("accreditation", "document-uploads");
              }
              return sectionsToCheck.every(sectionId => {
                const status = adequacyChecks[sectionId]?.status;
                return status === "Adequate" || status === "To be Verify";
              });
            })() && (
            <>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setDialogType("generate-form-11");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Generate Form 11
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setDialogType("go-for-assessment");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Go For Assessment
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setDialogType("process-completed");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Process Completed
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Password Dialog */}
      <Dialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* <TerminateApplicationDialog appId={appId} stage={stage} /> */}
    </Box>
  );
};

export default AcquisitionMerger;
